/**
 * The round "Need help?" button in the bottom right of every signed-in page,
 * and the panel it opens. Ported from ChemZone ERP's support launcher.
 *
 * Three views in one panel: the list of queries, a new query, and one
 * query's thread. The page the person was on rides along with a new query
 * automatically, so "it does not work" arrives with a where. While a
 * screenshot box is being dragged the panel steps aside.
 *
 * Everyone sees and answers only their own queries. The LoopDesk team (our
 * own team, `is_loopdesk_team`) sees every query, replies, and is the only
 * one with Mark as done, Close and Reopen. DrTutor admins see every query
 * too, but read only, apart from the queries they sent themselves.
 *
 * Opening from elsewhere: a notification links to `?query=<id>` on any page,
 * and any screen can dispatch `drtutor:open-support` (optionally with
 * `{ detail: { queryId } }`).
 */

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, Check, ChevronRight, Image as ImageIcon, LifeBuoy, Plus, RotateCcw, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { AuthImage, openFile } from "@/components/viewer";
import { useAuth } from "@/hooks/useAuth";
import { pushToast } from "@/hooks/useToast";
import { ApiError } from "@/lib/api/errors";
import {
  useReplyToQuery,
  useSendQuery,
  useSetQueryStatus,
  useSupportCounts,
  useSupportQueries,
  useSupportQuery,
  type QueryMessage,
  type QueryStatus,
  type SupportQueryDetail,
} from "@/lib/api/support";
import { timeAgo } from "@/lib/format/time";
import { Pill } from "@/primitives/surface";
import { Button } from "@/primitives/ui/Button";
import { useTenant } from "@/lib/tenant";

import { SupportComposer } from "./SupportComposer";
import { isLoopDeskTeam, OPEN_SUPPORT_EVENT, ROLE_LABEL, seesAllQueries, STATUS_TONE } from "./supportFormat";
import "./support.css";

type View = { name: "list" } | { name: "new" } | { name: "thread"; id: number };
type Scope = "all" | "mine";
type StatusFilter = QueryStatus | "live";


function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const first = err.errors ? Object.values(err.errors)[0] : undefined;
    const msg = Array.isArray(first) ? first[0] : first;
    return (typeof msg === "string" && msg) || err.message || fallback;
  }
  return fallback;
}

function initials(name: string | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

/* ─────────────────────────────────────────── Launcher */

export function SupportLauncher() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>({ name: "list" });
  const [picking, setPicking] = useState(false);
  const reduce = useReducedMotion();
  const [params, setParams] = useSearchParams();

  // A notification link (`?query=12`) opens that query, then tidies the URL.
  useEffect(() => {
    const raw = params.get("query");
    if (!raw) return;
    const id = Number(raw);
    if (Number.isInteger(id) && id > 0) {
      setView({ name: "thread", id });
      setOpen(true);
    }
    const next = new URLSearchParams(params);
    next.delete("query");
    setParams(next, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const id = (e as CustomEvent<{ queryId?: number }>).detail?.queryId;
      setView(id ? { name: "thread", id } : { name: "new" });
      setOpen(true);
    };
    window.addEventListener(OPEN_SUPPORT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SUPPORT_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!open || picking) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !document.querySelector("[role=dialog][aria-modal=true]")) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, picking]);

  if (!user) return null;

  return (
    <div data-screenshot-ignore="" className="sq">
      <AnimatePresence>
        {open ? (
          <motion.section
            key="panel"
            role="dialog"
            aria-label="Help and queries"
            className="sq-panel"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 12, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 420, damping: 34 }}
            // Hidden, not unmounted, while a box is dragged: the draft lives here.
            style={{ visibility: picking ? "hidden" : "visible" }}
          >
            <Panel view={view} setView={setView} onClose={() => setOpen(false)} onPickingChange={setPicking} />
          </motion.section>
        ) : null}
      </AnimatePresence>

      <motion.button
        type="button"
        className="sq-launch"
        data-open={open || undefined}
        hidden={picking}
        aria-label={open ? "Close help" : "Need help? Send us a query"}
        aria-expanded={open}
        title={open ? "Close help" : "Need help? Send us a query"}
        whileTap={reduce ? undefined : { scale: 0.94 }}
        onClick={() => {
          if (!open) setView({ name: "list" });
          setOpen((v) => !v);
        }}
      >
        {!open && !reduce ? <span className="sq-launch__pulse" aria-hidden /> : null}
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={open ? "close" : "open"}
            className="sq-launch__icon"
            initial={reduce ? false : { rotate: -45, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={reduce ? undefined : { rotate: 45, opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {open ? <X size={22} strokeWidth={2} /> : <LifeBuoy size={22} strokeWidth={2} />}
          </motion.span>
        </AnimatePresence>
      </motion.button>
    </div>
  );
}

/* ─────────────────────────────────────────── Panel */

function Panel({
  view,
  setView,
  onClose,
  onPickingChange,
}: {
  view: View;
  setView: (v: View) => void;
  onClose: () => void;
  onPickingChange: (picking: boolean) => void;
}) {
  const title = view.name === "new" ? "New query" : view.name === "thread" ? "Query" : "Help";
  const sub =
    view.name === "new" ? "We'll see the page you're on." : "Something not working, or need a change? Tell us here.";

  return (
    <>
      <header className="sq-head">
        {view.name !== "list" ? (
          <button type="button" className="sq-icon-btn sq-icon-btn--on-dark" aria-label="Back to queries" onClick={() => setView({ name: "list" })}>
            <ArrowLeft size={17} strokeWidth={2} />
          </button>
        ) : (
          <span className="sq-head__badge" aria-hidden>
            <LifeBuoy size={16} strokeWidth={2} />
          </span>
        )}
        <div className="sq-head__text">
          <p className="sq-head__title">{title}</p>
          <p className="sq-head__sub">{sub}</p>
        </div>
        <button type="button" className="sq-icon-btn sq-icon-btn--on-dark" aria-label="Close help" onClick={onClose}>
          <X size={17} strokeWidth={2} />
        </button>
      </header>

      <div className="sq-body">
        {view.name === "list" ? (
          <QueryList onOpen={(id) => setView({ name: "thread", id })} onNew={() => setView({ name: "new" })} />
        ) : view.name === "new" ? (
          <NewQuery onPickingChange={onPickingChange} onSent={(id) => setView({ name: "thread", id })} />
        ) : (
          <Thread id={view.id} onPickingChange={onPickingChange} />
        )}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────── List */

function QueryList({ onOpen, onNew }: { onOpen: (id: number) => void; onNew: () => void }) {
  const { user } = useAuth();
  const seesAll = seesAllQueries(user);
  const [scope, setScope] = useState<Scope>("all");
  const [status, setStatus] = useState<StatusFilter>("live");

  const counts = useSupportCounts(seesAll);
  const list = useSupportQueries({
    page_size: 50,
    ordering: "-last_activity_at",
    mine: seesAll && scope === "mine" ? true : undefined,
    status: seesAll && status !== "live" ? status : undefined,
  });
  // "Needs a reply" is open + in progress, filtered here so one request does.
  const rows = (list.data?.results ?? []).filter(
    (r) => !seesAll || status !== "live" || r.status === "open" || r.status === "in_progress",
  );

  const filters: Array<{ value: StatusFilter; label: string; count?: number }> = [
    { value: "live", label: "Needs a reply", count: (counts.data?.open ?? 0) + (counts.data?.in_progress ?? 0) },
    { value: "resolved", label: "Done", count: counts.data?.resolved },
    { value: "closed", label: "Closed", count: counts.data?.closed },
  ];

  return (
    <div className="sq-list">
      <button type="button" className="sq-new" onClick={onNew}>
        <span className="sq-new__icon" aria-hidden>
          <Plus size={18} strokeWidth={2} />
        </span>
        <span className="sq-new__text">
          <span className="sq-new__title">Send a new query</span>
          <span className="sq-new__sub">Tell us what you need. Add a screenshot if it helps.</span>
        </span>
        <ChevronRight size={16} strokeWidth={2} className="sq-new__chev" aria-hidden />
      </button>

      {seesAll ? (
        <div className="sq-filters">
          <div className="sq-tabs" role="tablist" aria-label="Whose queries">
            {(["all", "mine"] as const).map((s) => (
              <button key={s} type="button" role="tab" aria-selected={scope === s} onClick={() => setScope(s)}>
                {s === "all" ? "Everyone's" : "Mine"}
              </button>
            ))}
          </div>
          <div className="sq-chips" role="group" aria-label="Status">
            {filters.map((f) => (
              <button key={f.value} type="button" aria-pressed={status === f.value} onClick={() => setStatus(f.value)}>
                {f.label}
                {f.count !== undefined && scope === "all" ? <span className="sq-chips__n">{f.count}</span> : null}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p className="sq-section">Your queries</p>
      )}

      {list.isPending ? (
        <div className="sq-rows" aria-hidden>
          {[0, 1, 2].map((i) => (
            <span key={i} className="skeleton sq-row-skel" />
          ))}
        </div>
      ) : list.isError ? (
        <div className="sq-empty">
          <p className="sq-empty__title">Your queries didn't load.</p>
          <Button variant="ghost" size="sm" onClick={() => void list.refetch()}>
            Try again
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="sq-empty">
          <p className="sq-empty__title">{seesAll ? "Nothing here right now." : "Nothing sent yet."}</p>
          <p className="sq-empty__body">
            {seesAll ? "New queries from tutors, students and families land here." : "When you send a query, our replies show up here."}
          </p>
        </div>
      ) : (
        <ul className="sq-rows">
          {rows.map((r) => (
            <li key={r.id}>
              <button type="button" className="sq-row" onClick={() => onOpen(r.id)}>
                <span className="sq-row__top">
                  <span className="sq-row__num">{r.number}</span>
                  {seesAll && r.reporter ? (
                    <span className="sq-row__who">
                      {r.reporter.name}
                      {r.reporter.role ? ` · ${ROLE_LABEL[r.reporter.role] ?? r.reporter.role}` : ""}
                    </span>
                  ) : null}
                  <span className="sq-row__status">
                    <Pill tone={STATUS_TONE[r.status]}>{r.status_label}</Pill>
                  </span>
                </span>
                <span className="sq-row__subject">{r.subject}</span>
                <span className="sq-row__meta">
                  <span className="sq-row__last">{r.last_message || "Screenshot"}</span>
                  {r.has_screenshot ? <ImageIcon size={13} strokeWidth={1.75} aria-label="Has a screenshot" /> : null}
                  <span className="sq-row__time">{timeAgo(r.last_activity_at)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────── New query */

function NewQuery({ onPickingChange, onSent }: { onPickingChange: (p: boolean) => void; onSent: (id: number) => void }) {
  const { pathname, search } = useLocation();
  const send = useSendQuery();

  return (
    <div className="sq-new-form">
      <SupportComposer
        autoFocus
        minHeight={132}
        placeholder="What do you need? For example: the homework page won't open on my phone."
        submitLabel="Send query"
        onPickingChange={onPickingChange}
        onSubmit={async (draft) => {
          try {
            const q = await send.mutateAsync({ ...draft, pageUrl: `${pathname}${search}` });
            pushToast({ variant: "success", title: `Sent as ${q.number}.`, body: "We'll reply here, and you'll get a notification." });
            onSent(q.id);
          } catch (err) {
            pushToast({ variant: "danger", title: "Your query didn't send.", body: errorText(err, "Check your connection and try again.") });
            throw err;
          }
        }}
      />
      <p className="sq-hint">
        Sent from <span className="sq-hint__path">{pathname}</span>. Press Ctrl + Enter to send.
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────── Thread */

function Bubble({ message, mine }: { message: QueryMessage; mine: boolean }) {
  const { displayName } = useTenant();
  const name = mine ? "You" : (message.author?.name ?? displayName);
  return (
    <li className="sq-msg" data-mine={mine || undefined} data-team={message.is_team || undefined}>
      <span className="sq-msg__avatar" aria-hidden>
        {initials(message.author?.name)}
      </span>
      <div className="sq-msg__col">
        <div className="sq-msg__meta">
          <span className="sq-msg__name">{name}</span>
          {message.is_team ? <span className="sq-msg__team">LoopDesk team</span> : null}
          <time dateTime={message.created_at}>{timeAgo(message.created_at)}</time>
        </div>
        {message.body ? <div className="sq-msg__bubble">{message.body}</div> : null}
        {message.screenshot_url ? (
          <button
            type="button"
            className="sq-msg__shot"
            aria-label="Open the screenshot"
            onClick={() => void openFile({ path: message.screenshot_url as string, name: "Screenshot.png" })}
          >
            <AuthImage path={message.screenshot_url} alt="Screenshot" />
          </button>
        ) : null}
      </div>
    </li>
  );
}

function TeamActions({ query }: { query: SupportQueryDetail }) {
  const setStatus = useSetQueryStatus(query.id);
  const move = async (status: QueryStatus, done: string) => {
    try {
      await setStatus.mutateAsync({ status });
      pushToast({ variant: "success", title: done });
    } catch (err) {
      pushToast({ variant: "danger", title: "That didn't save.", body: errorText(err, "Check your connection and try again.") });
    }
  };
  const live = query.status === "open" || query.status === "in_progress";
  return (
    <div className="sq-actions">
      {live ? (
        <>
          <Button variant="primary" size="sm" leadingIcon={Check} loading={setStatus.isPending} onClick={() => void move("resolved", `${query.number} marked as done.`)}>
            Mark as done
          </Button>
          <Button variant="ghost" size="sm" disabled={setStatus.isPending} onClick={() => void move("closed", `${query.number} closed.`)}>
            Close
          </Button>
        </>
      ) : (
        <Button variant="ghost" size="sm" leadingIcon={RotateCcw} loading={setStatus.isPending} onClick={() => void move("open", `${query.number} reopened.`)}>
          Reopen
        </Button>
      )}
    </div>
  );
}

function Thread({ id, onPickingChange }: { id: number; onPickingChange: (p: boolean) => void }) {
  const { user } = useAuth();
  const seesAll = seesAllQueries(user);
  const isTeam = isLoopDeskTeam(user);
  const query = useSupportQuery(id);
  const reply = useReplyToQuery(id);
  const endRef = useRef<HTMLDivElement>(null);
  const q = query.data;

  // A block body on purpose: newer browsers make scrollIntoView return a
  // Promise, and an effect may only return a cleanup function.
  const messageCount = q?.messages.length ?? 0;
  useEffect(() => {
    if (messageCount > 0) endRef.current?.scrollIntoView({ block: "end" });
  }, [messageCount]);

  if (query.isPending) {
    return (
      <div className="sq-thread" aria-hidden>
        <span className="skeleton" style={{ display: "block", height: 18, width: "60%", borderRadius: 6 }} />
        <span className="skeleton" style={{ display: "block", height: 64, width: "80%", borderRadius: 14 }} />
        <span className="skeleton" style={{ display: "block", height: 48, width: "60%", borderRadius: 14, marginLeft: "auto" }} />
      </div>
    );
  }
  if (!q) {
    return (
      <div className="sq-empty">
        <p className="sq-empty__title">This query didn't load.</p>
        <p className="sq-empty__body">It may have been sent from another account.</p>
        <Button variant="ghost" size="sm" onClick={() => void query.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const done = q.status === "resolved" || q.status === "closed";
  const mineIsReporter = String(q.reporter?.id) === String(user?.id);

  return (
    <div className="sq-thread">
      <div className="sq-thread__head">
        <div className="sq-thread__line">
          <span className="sq-row__num">{q.number}</span>
          <Pill tone={STATUS_TONE[q.status]}>{q.status_label}</Pill>
        </div>
        <p className="sq-thread__subject">{q.subject}</p>
        {seesAll && q.reporter ? (
          <p className="sq-thread__from">
            From {q.reporter.name}
            {q.reporter.role ? ` (${ROLE_LABEL[q.reporter.role] ?? q.reporter.role})` : ""}
            {q.page_url ? (
              <>
                {" "}on <span className="sq-hint__path">{q.page_url}</span>
              </>
            ) : null}
          </p>
        ) : null}
        {isTeam ? <TeamActions query={q} /> : null}
      </div>

      <ol className="sq-msgs" aria-label={`Messages on ${q.number}`}>
        {q.messages.map((m) => (
          <Bubble key={m.id} message={m} mine={String(m.author?.id) === String(user?.id)} />
        ))}
      </ol>
      <div ref={endRef} />

      {done && mineIsReporter ? (
        <p className="sq-done">
          {q.status === "resolved" ? "We've marked this as done." : "This query is closed."} Still not right? Reply below and it opens again.
        </p>
      ) : null}

      {isTeam || mineIsReporter ? (
        <SupportComposer
          placeholder={isTeam && !mineIsReporter ? "Reply to them" : "Write a reply"}
          submitLabel="Reply"
          minHeight={72}
          onPickingChange={onPickingChange}
          onSubmit={async (draft) => {
            try {
              await reply.mutateAsync(draft);
            } catch (err) {
              pushToast({ variant: "danger", title: "Your reply didn't send.", body: errorText(err, "Check your connection and try again.") });
              throw err;
            }
          }}
        />
      ) : (
        <p className="sq-readonly">Only the LoopDesk team can reply to this query or change its status.</p>
      )}
    </div>
  );
}
